import { Component, Input, OnInit, signal, computed, effect, inject, DestroyRef } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ConfigService } from '../../services/config.service';
import { LoginIbero } from "../ibero/login-ibero/login-ibero";
import { environment } from '../../../environments/environment';

// Type definitions for better type safety
interface PlaceConfig {
  id: number;
  logo: string;
  name: string;
  login_required: boolean;
}

interface AppConfig {
  primary_color: string;
}

interface ConfigResponse {
  data: {
    place: PlaceConfig;
    config: AppConfig;
  };
}

interface AuthCallbackResponse {
  success: boolean;
  error?: any;
}

interface UserInfo {
  givenName: string;
  [key: string]: any;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, NgOptimizedImage, FormsModule, LoginIbero],
  templateUrl: './login.html',
  styleUrl: './login.css'
})
export class Login implements OnInit {
  // Dependency injection using inject() (Angular 14+)
  private readonly router = inject(Router);
  private readonly configService = inject(ConfigService);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  // Input with default value
  @Input() gsid!: number;

  // Signals for reactive state management (Angular 16+)
  public showContent = signal<boolean>(false);
  public dataConfig = signal<ConfigResponse['data'] | null>(null);
  public phoneIndicative = signal<string>('+57');
  public phoneNumber = signal<string>('');
  public isLoading = signal<boolean>(false);
  public error = signal<string | null>(null);
  public isAuthenticating = signal<boolean>(false);

  // Environment variables
  public readonly urlFrontEnd = environment.urlFrontEnd;
  public readonly apiUrlSSO = environment.apiUrlSSO;

  // Computed signals for derived state
  public isIberoPlace = computed(() => {
    const config = this.dataConfig();
    if (!config) return false;
    const iberoIds = [991, 1007, 876];
    return iberoIds.includes(config.place.id);
  });

  public isFormValid = computed(() => {
    const phone = this.phoneNumber();
    return phone.length >= 10 && /^\d+$/.test(phone);
  });

  public canSubmit = computed(() => {
    return this.isFormValid() && !this.isLoading() && !this.isAuthenticating();
  });

  constructor() {
    // Effect to log phone number changes (for debugging)
    effect(() => {
      const phone = this.phoneNumber();
      if (phone) {
        console.log('Phone number updated:', phone);
      }
    }, { allowSignalWrites: false });
  }

  ngOnInit(): void {
    this.initializeLogin();
  }

  private initializeLogin(): void {
    // Use takeUntilDestroyed for automatic unsubscription
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (params) => {
          if (params['tokens']) {
            this.handleAuthResponse(params['tokens']);
          } else {
            console.log('No tokens found in URL');
            console.log('Component Login ==> GSID:', this.gsid);
            
            if (this.gsid) {
              this.getConfiguration(this.gsid);
            } else {
              console.error('GSID is not provided');
              this.error.set('GSID no proporcionado');
            }
          }
        },
        error: (error) => {
          console.error('Error reading query params:', error);
          this.error.set('Error al leer parámetros de la URL');
        }
      });
  }

  private getConfiguration(gsid: number): void {
    console.log("2 ==> Getting configuration for gsid:", gsid);
    this.isLoading.set(true);
    this.error.set(null);

    this.configService.getConfiguration(gsid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isLoading.set(false);
          
          if (!response?.data) {
            this.error.set('Respuesta inválida del servidor');
            return;
          }

          // Update signals properly
          this.dataConfig.set(response.data);
          
          // Store data in localStorage
          this.storeConfigData(response.data);

          if (response.data.place.login_required) {
            console.log('Login is required');
            this.showContent.set(true);
          } else {
            console.log('Login is not required');
            this.router.navigate(['home']);
          }
        },
        error: (error) => {
          console.error('Error getting configuration:', error);
          this.isLoading.set(false);
          this.error.set('Error al obtener configuración');
        }
      });
  }

  private storeConfigData(data: ConfigResponse['data']): void {
    try {
      localStorage.setItem('gslogo', data.place.logo);
      localStorage.setItem('gsname', data.place.name);
      localStorage.setItem('gscolor', data.config.primary_color);
    } catch (error) {
      console.error('Error storing config data:', error);
    }
  }

  private handleAuthResponse(tokens: string): void {
    console.log('handleAuthResponse', tokens);
    this.isAuthenticating.set(true);
    this.error.set(null);

    this.authService.handleAuthCallback(tokens)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: AuthCallbackResponse) => {
          if (response.success) {
            const user = this.authService.getUserInfo() as UserInfo;
            console.log('Usuario autenticado:', user);
            
            if (user?.givenName) {
              console.log(`Bienvenido ${user.givenName}!`);
            }
            
            this.userAuthorize();
          } else {
            console.error('Error en autenticación:', response.error);
            this.isAuthenticating.set(false);
            this.error.set('Error en la autenticación');
            this.router.navigate(['/login']);
          }
        },
        error: (error) => {
          console.error('Error processing auth response:', error);
          this.isAuthenticating.set(false);
          this.error.set('Error procesando respuesta de autenticación');
          this.router.navigate(['/login']);
        }
      });
  }

  private userAuthorize(): void {
    console.log('userAuthorize()');
    
    this.authService.getGuestAccessAuthorize()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isAuthenticating.set(false);
          
          if (response?.data?.user?.id > 0) {
            console.log('Usuario autorizado, redirigiendo a /home:', response);
            
            try {
              localStorage.setItem('gstoken', response.data.login.token);
              localStorage.setItem('gsuid', response.data.user.id.toString());
              this.router.navigate(['/home']);
            } catch (error) {
              console.error('Error storing auth data:', error);
              this.error.set('Error guardando datos de sesión');
            }
          } else {
            console.error('Error en autenticación', response.error);
            this.error.set('Usuario no autorizado');
            this.router.navigate(['/login']);
          }
        },
        error: (error) => {
          console.error('Error processing authorize response:', error);
          this.isAuthenticating.set(false);
          this.error.set('Error en la autorización');
          this.router.navigate(['/login']);
        }
      });
  }

  public loginWithGoogle(): void {
    const phone = this.phoneNumber();
    
    if (!phone) {
      console.error('Número de teléfono requerido');
      this.error.set('Por favor ingresa tu número de teléfono');
      return;
    }

    if (!this.isFormValid()) {
      this.error.set('Por favor ingresa un número de teléfono válido');
      return;
    }

    this.isLoading.set(true);
    
    try {
      const baseUrl = `${this.apiUrlSSO}/auth/login/google`;
      const finalRedirectUri = encodeURIComponent(`${this.urlFrontEnd}/${this.gsid}`);
      const encodedPhone = encodeURIComponent(phone);
      const url = `${baseUrl}?phone=${encodedPhone}&final_redirect_uri=${finalRedirectUri}`;
      
      // Redirect to Google login
      window.location.href = url;
    } catch (error) {
      console.error('Error redirecting to Google login:', error);
      this.isLoading.set(false);
      this.error.set('Error al iniciar sesión con Google');
    }
  }

  // Public method to clear error
  public clearError(): void {
    this.error.set(null);
  }

  // Public method to validate phone number
  public validatePhoneNumber(phone: string): boolean {
    return /^\d{10,15}$/.test(phone);
  }
}



